import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { TreatmentNameToInitialQuestionnaireIDMapping } from "@/constants/Mappings";
import { type Treatment } from "@/constants/treatments";
import Link from "next/link";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";

const Pricing = ({ treatment }: { treatment: Treatment }) => {
    const router = useRouter();
    const initialQuestionnaireId =
        TreatmentNameToInitialQuestionnaireIDMapping[treatment.name];

    const PriceBox = ({
        title,
        description,
        price,
    }: {
        title: string;
        description: string[];
        price: string;
    }) => (
        <div className="w-full relative p-2">
            <div className="text-center text-[#333333] dark:text-white text-2xl md:text-[28px] font-medium font-satoshi leading-[30.80px]">
                {title}
            </div>
            <div className="flex flex-col items-center gap-1 mt-4 md:mt-5">
                <div className="opacity-70 text-center text-[#1a5b77] dark:text-blue-300 text-sm md:text-base font-light font-space leading-tight">
                    {description.map((line, index) => (
                        <span key={index}>
                            {line}
                            <br />
                        </span>
                    ))}
                </div>
                <div className="text-center text-[#1a5b77] dark:text-blue-300 text-4xl md:text-[50px] font-light font-space leading-[60px]">
                    {price}
                </div>
            </div>
        </div>
    );

    const secondDescription = treatment?.pricing.prescriptionLength
        ? [
              `Estimated ${treatment.pricing.prescriptionLength} day supply price`,
              "you pay at pharmacy",
          ]
        : ["Estimated 90 day supply price", "you pay at pharmacy"];

    const thirdDescription = treatment?.carecard
        ? ["With the CareCard", "discount"]
        : ["With the RTHM", "discount"];

    const thirdTitle = treatment?.pricing.prescriptionLength
        ? `Estimated Total for ${treatment.pricing.prescriptionLength} days`
        : `Estimated Total for 90 days`;

    return (
        <div className="flex flex-col items-center">
            <h2 className="text-[#333333] dark:text-white text-3xl md:text-5xl font-semibold font-satoshi uppercase mb-12 leading-tight text-center">
                Affordable Access to {treatment.name}
            </h2>

            <div className="w-full flex flex-col items-center gap-4">
                {/* Pricing Details */}
                <Card className="w-full p-4 md:p-8">
                    <CardContent className="p-0">
                        <div className="flex flex-col md:flex-row md:justify-between">
                            <PriceBox
                                title="RTHM"
                                description={[
                                    "Online assessment fee for prescription",
                                    "Only pay if prescription is approved",
                                ]}
                                price={treatment.pricing.consultationPrice}
                            />

                            {/* Divider that only shows on desktop */}
                            <div className="hidden md:block w-px h-[137px] bg-[#333333]/20 dark:bg-white/20" />

                            {/* Mobile divider */}
                            <div className="md:hidden w-full h-px bg-[#333333]/20 dark:bg-white/20 my-4" />

                            <PriceBox
                                title="Pharmacy Cost"
                                description={secondDescription}
                                price={treatment.pricing.pharmacyCost}
                            />

                            {/* Divider that only shows on desktop */}
                            <div className="hidden md:block w-px h-[137px] bg-[#333333]/20 dark:bg-white/20" />

                            {/* Mobile divider */}
                            <div className="md:hidden w-full h-px bg-[#333333]/20 dark:bg-white/20 my-4" />

                            <PriceBox
                                title={thirdTitle}
                                description={thirdDescription}
                                price={treatment.pricing.estimate}
                            />
                        </div>
                    </CardContent>

                    {/* Buttons */}
                    <CardFooter className="flex flex-col md:flex-row justify-center gap-4 mt-8 p-0">
                        {initialQuestionnaireId && (
                            <Button
                                id={
                                    "start-safety-assessment-pricing-" +
                                    treatment.name
                                }
                                onClick={() => {
                                    posthog?.capture(
                                        "start_safety_assessment",
                                        {
                                            part: "pricing",
                                            treatmentName: treatment.name,
                                        },
                                    );
                                    router.push(
                                        `/orders/${initialQuestionnaireId}`,
                                    );
                                }}
                                className="w-full lg:w-fit h-14 px-12 uppercase"
                            >
                                Start safety assessment
                            </Button>
                        )}
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
};

export default Pricing;
